import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle,
  HelpCircle,
  ChevronRight,
  Edit3,
  MessageSquare,
} from 'lucide-react';
import { extractVariablesFromText, ExtractedVariables } from '../services/claudeAI';

interface AISmartFillProps {
  isOpen: boolean;
  onClose: () => void;
  templateVariables: Array<{
    name: string;
    type: string;
    required: boolean;
    label?: string;
  }>;
  onFill: (variables: Record<string, any>) => void;
}

export function AISmartFill({
  isOpen,
  onClose,
  templateVariables,
  onFill,
}: AISmartFillProps) {
  const [input, setInput] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [result, setResult] = useState<ExtractedVariables | null>(null);
  const [editedVariables, setEditedVariables] = useState<Record<string, any>>({});
  const [error, setError] = useState('');
  const [step, setStep] = useState<'input' | 'review'>('input');

  const handleExtract = async () => {
    if (!input.trim()) {
      setError('Please describe the document details');
      return;
    }

    setError('');
    setIsExtracting(true);

    try {
      const extracted = await extractVariablesFromText(input, templateVariables);
      setResult(extracted);
      setEditedVariables(extracted.variables);
      setStep('review');
    } catch (err) {
      console.error('Extraction error:', err);
      setError('Failed to extract variables. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleApply = () => {
    onFill(editedVariables);
    handleClose();
  };

  const handleClose = () => {
    setInput('');
    setResult(null);
    setEditedVariables({});
    setError('');
    setStep('input');
    onClose();
  };

  const updateVariable = (name: string, value: any) => {
    setEditedVariables((prev) => ({ ...prev, [name]: value }));
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-400';
    if (confidence >= 0.5) return 'text-amber-400';
    return 'text-red-400';
  };

  const examplePrompts = [
    'John Smith is selling his house at 123 Main Street, Milwaukee, WI 53202 to Jane Doe for $350,000. The closing date is January 15, 2025. The property is in Milwaukee County, parcel number 123-456-789.',
    'We\'re creating an LLC called "Smith Holdings LLC" with two members: John Smith (60% ownership, $60,000 contribution) and Jane Smith (40%, $40,000). It will be member-managed.',
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">AI Smart Fill</h2>
                  <p className="text-sm text-slate-400">
                    Describe your document and AI will fill in the variables
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {step === 'input' && (
                <div className="space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Describe the document details in plain English
                    </label>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Example: John Smith is selling his house at 123 Main Street to Jane Doe for $350,000. The closing is on January 15, 2025..."
                      rows={6}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none"
                    />
                  </div>

                  {/* Variables that will be extracted */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
                      Variables to extract ({templateVariables.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {templateVariables.slice(0, 10).map((v) => (
                        <span
                          key={v.name}
                          className={`text-xs px-2 py-1 rounded-full ${
                            v.required
                              ? 'bg-amber-500/20 text-amber-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {v.label || v.name}
                          {v.required && ' *'}
                        </span>
                      ))}
                      {templateVariables.length > 10 && (
                        <span className="text-xs px-2 py-1 text-slate-500">
                          +{templateVariables.length - 10} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Examples */}
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">
                      Example descriptions
                    </p>
                    <div className="space-y-2">
                      {examplePrompts.map((prompt, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(prompt)}
                          className="block w-full text-left p-3 text-sm text-slate-400 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors"
                        >
                          "{prompt.slice(0, 100)}..."
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {isExtracting && (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Extracting Variables
                  </h3>
                  <p className="text-slate-400">
                    AI is analyzing your description...
                  </p>
                </div>
              )}

              {step === 'review' && result && (
                <div className="space-y-6">
                  {/* Confidence */}
                  <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-emerald-400" />
                      <span className="text-white font-medium">Extraction Confidence</span>
                    </div>
                    <span className={`text-lg font-bold ${getConfidenceColor(result.confidence)}`}>
                      {Math.round(result.confidence * 100)}%
                    </span>
                  </div>

                  {/* Clarifications */}
                  {result.clarifications.length > 0 && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-400 mb-2">
                        <HelpCircle className="w-4 h-4" />
                        <span className="font-medium">Clarifications Needed</span>
                      </div>
                      <ul className="space-y-1 text-sm text-amber-300/80">
                        {result.clarifications.map((q, i) => (
                          <li key={i}>• {q}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Extracted Variables */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-3">
                      Review & Edit Extracted Values
                    </h4>
                    <div className="space-y-3">
                      {templateVariables.map((variable) => {
                        const value = editedVariables[variable.name];
                        const hasValue = value !== undefined && value !== '';
                        
                        return (
                          <div
                            key={variable.name}
                            className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-slate-300">
                                {variable.label || variable.name}
                                {variable.required && (
                                  <span className="text-red-400 ml-1">*</span>
                                )}
                              </label>
                              {hasValue ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-slate-500" />
                              )}
                            </div>
                            {variable.type === 'boolean' ? (
                              <select
                                value={value ? 'true' : 'false'}
                                onChange={(e) =>
                                  updateVariable(variable.name, e.target.value === 'true')
                                }
                                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                              >
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                              </select>
                            ) : variable.type === 'textarea' ? (
                              <textarea
                                value={value || ''}
                                onChange={(e) => updateVariable(variable.name, e.target.value)}
                                rows={3}
                                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 resize-none"
                              />
                            ) : (
                              <input
                                type={variable.type === 'number' ? 'number' : 'text'}
                                value={value || ''}
                                onChange={(e) =>
                                  updateVariable(
                                    variable.name,
                                    variable.type === 'number'
                                      ? parseFloat(e.target.value)
                                      : e.target.value
                                  )
                                }
                                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                                placeholder={`Enter ${variable.label || variable.name}...`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-slate-700">
              {step === 'input' && (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExtract}
                    disabled={isExtracting}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    Extract Variables
                  </button>
                </>
              )}

              {step === 'review' && (
                <>
                  <button
                    onClick={() => setStep('input')}
                    className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleApply}
                    className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-lg font-medium transition-colors"
                  >
                    Apply Values
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default AISmartFill;
